import React from "react";
import { Modal, Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { motion, AnimatePresence } from "framer-motion";

/**
 * ServiceModal optimizado con React.memo
 * Solo se re-renderiza si service o isOpen cambian
 */
const ServiceModal = React.memo(
  ({ service, isOpen, onClose }) => {
    if (!service) return null;

    const modalVariants = {
      hidden: {
        opacity: 0,
        scale: 0.8,
        y: 50,
      },
      visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
          duration: 0.3,
          ease: [0.25, 0.46, 0.45, 0.94],
        },
      },
      exit: {
        opacity: 0,
        scale: 0.8,
        y: 50,
        transition: {
          duration: 0.2,
        },
      },
    };

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        aria-labelledby={`modal-${service.title}`}
        aria-describedby={`modal-description-${service.title}`}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AnimatePresence mode="wait">
          {isOpen && (
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <Box
                sx={{
                  position: "relative",
                  width: "80%",
                  maxWidth: "600px",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  p: 4,
                  borderRadius: 2,
                  outline: "none",
                  maxHeight: "90vh",
                  overflowY: "auto",
                }}
              >
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "rotate(90deg)",
                      color: "error.main",
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <img
                  src={service.image}
                  alt={service.title}
                  style={{
                    width: "100%",
                    height: "auto",
                    marginBottom: "16px",
                    borderRadius: "8px",
                  }}
                  loading="lazy"
                />
                <Typography variant="h5" gutterBottom className="animated-gradient-text">
                  {service.title}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {service.modalContent.details}
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Key Features:
                </Typography>
                <ul>
                  {service.modalContent.features.map((feature, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                    >
                      <Typography variant="body1">{feature}</Typography>
                    </motion.li>
                  ))}
                </ul>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Modal>
    );
  },
  // Solo re-renderizar si service o isOpen cambian
  (prevProps, nextProps) => {
    return (
      prevProps.service?.key === nextProps.service?.key &&
      prevProps.isOpen === nextProps.isOpen
    );
  }
);

ServiceModal.displayName = "ServiceModal";

export default ServiceModal;
